/**
 * Klien penghantaran syif ke pelayan.
 *
 * Syif dihantar selepas ia ditutup (dan sekali semasa dibuka), supaya back office boleh melihat
 * Z-report. Ia **tidak** menyekat jualan: jika rangkaian mati, syif disimpan dengan
 * `uploaded_at = null` dan dihantar pada sync berikutnya.
 */

import { signPayload } from './sync.js';
import type { ShiftRecord } from './shift.js';
import type { DeviceIdentity } from './types.js';

export interface ShiftUpload {
  id: string;
  client_seq: number;
  opened_at: string;
  closed_at: string | null;
  opening_cash_sen: number;
  closing_cash_sen: number | null;
  status: 'open' | 'closed';
}

/** Tukar rekod syif setempat kepada bentuk yang diterima pelayan. */
export function toUpload(shift: ShiftRecord, clientSeq: number): ShiftUpload {
  return {
    id: shift.id,
    client_seq: clientSeq,
    opened_at: shift.opened_at,
    closed_at: shift.closed_at,
    opening_cash_sen: shift.opening_cash_sen,
    closing_cash_sen: shift.counted_cash_sen,
    status: shift.status,
  };
}

/**
 * Hantar syif ke pelayan.
 *
 * Pulangkan `true` jika diterima. Kegagalan **tidak** dilempar: syif kekal setempat dan akan
 * dihantar semula, kerana kehilangan Z-report tidak boleh menghalang kedai menutup syif.
 */
export async function uploadShift(
  apiBase: string,
  identity: DeviceIdentity,
  privateKey: string | CryptoKey,
  shift: ShiftRecord,
  clientSeq: number,
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  const raw = JSON.stringify({ shifts: [toUpload(shift, clientSeq)] });
  const timestamp = Math.floor(Date.now() / 1000);
  const signature = await signPayload(privateKey, timestamp, raw);

  try {
    const response = await fetchImpl(`${apiBase}/v1/sync/shifts`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'x-device-id': identity.deviceId,
        'x-device-timestamp': String(timestamp),
        'x-device-signature': signature,
      },
      body: raw,
    });

    if (!response.ok) return false;
    const body = (await response.json()) as { accepted?: string[] };
    return (body.accepted ?? []).includes(shift.id);
  } catch {
    return false;
  }
}
